import _ from 'lodash';
import { history } from 'umi';

import { apiRoute, getAll } from '@/services/api';
import { sortFn } from '@/utils/infrastructure';
import { getToken } from '@/utils/infrastructure/communication/request';
import { CoreEnums } from '@/utils/domain/constants';
import {
  SESSION_TYPES,
  STORAGE_KEYS,
  getCurrentSession,
  setCurrentSession,
  setItem,
  removeItem,
  migrateSession,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
} from '@/utils/infrastructure/auth';
import { menuIcons, routing } from './utils';

const initialState = {
  portal: null,
  menuIcons,
  retryTimes: 0,
  token: getToken({ global: true }),
};

const Model = {
  namespace: 'companyPortal',
  state: initialState,
  effects: {
    *auth({ payload }, { call, put, select }) {
      try {
        // Validate required parameters before calling API
        let { companyCode, portalCode } = payload || {};

        // If codes are missing, try to recover from stored config
        if (!companyCode || !portalCode) {
          try {
            const storedConfig = getSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
            if (storedConfig) {
              const parsed = JSON.parse(storedConfig);
              companyCode = companyCode || parsed.companyCode;
              portalCode = portalCode || parsed.portalCode;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }

        // If still missing required codes, abort authentication
        if (!companyCode || !portalCode) {
          console.warn('Portal auth aborted: missing companyCode or portalCode');
          const retryTimes = yield select((o) => o.companyPortal.retryTimes);
          if (!retryTimes || retryTimes < 3) {
            yield put({ type: 'queryRetry', payload: 1 });
          }
          return;
        }

        // Check if we're switching to a different portal - clean up user context
        const currentPortal = yield select((o) => o.companyPortal.portal);
        const isSwitchingPortal =
          currentPortal &&
          (currentPortal.companyCode !== companyCode || currentPortal.portalCode !== portalCode);

        if (isSwitchingPortal) {
          // Clear user context and portal config when switching portals
          const currentPortalSession = getCurrentSession();
          if (currentPortalSession?.type === SESSION_TYPES.PORTAL) {
            removeItem(
              SESSION_TYPES.PORTAL,
              currentPortalSession.userId,
              currentPortalSession.resourceId,
              STORAGE_KEYS.USER_CONTEXT,
            );
            removeItem(
              SESSION_TYPES.PORTAL,
              currentPortalSession.userId,
              currentPortalSession.resourceId,
              STORAGE_KEYS.PORTAL_CONFIG,
            );
          }
        }

        const config = yield call(apiRoute, {
          method: 'post',
          route: 'auth/portal',
          data: {
            password: portalCode,
            username: companyCode,
          },
        });

        // Migrate from automy → portal if switching contexts
        const currentSession = getCurrentSession();
        const userId = currentSession?.userId;
        const portalId = config?.portal?.id;

        if (userId && portalId) {
          if (currentSession.type === SESSION_TYPES.AUTOMY) {
            // Switching from automy to portal: migrate auth keys
            const portalSession = {
              type: SESSION_TYPES.PORTAL,
              userId,
              resourceId: String(portalId),
            };
            migrateSession(currentSession, portalSession);
            setCurrentSession(portalSession);
          } else if (
            currentSession.type !== SESSION_TYPES.PORTAL ||
            String(currentSession.resourceId) !== String(portalId)
          ) {
            // Switching between portals: just update session
            const portalSession = {
              type: SESSION_TYPES.PORTAL,
              userId,
              resourceId: String(portalId),
            };
            setCurrentSession(portalSession);
          }
        }

        yield put({
          type: 'queryAuth',
          payload: { ...config, companyCode, portalCode, ...payload, userId, portalId },
        });
      } catch (e) {
        const retryTimes = yield select((o) => o.companyPortal.retryTimes);
        if (!retryTimes || retryTimes < 3) {
          yield put({ type: 'queryRetry', payload: 1 });
        }
      }
    },

    *menu(o, { call, put }) {
      try {
        const res = yield call(getAll, { route: 'processPortal/menu' });
        yield put({ type: 'queryMenu', payload: res });

        // Auto-navigate to default embed page after menu loads
        const hasNavigatedEmbed = sessionStorage.getItem('x-portal-embed-navigated');
        const hasNavigatedTemplate = sessionStorage.getItem('x-portal-template-navigated');
        const logoutInProgress = sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS);

        if ((!hasNavigatedEmbed || !hasNavigatedTemplate) && !logoutInProgress && res?.length > 0) {
          // Find top-level items with default embed link
          const topLevelItems = res.reduce((acc, section) => {
            if (section.menuItems) {
              return [...acc, ...section.menuItems.filter((item) => !item.parent)];
            }
            return acc;
          }, []);

          const toMenuSlug = (value) => _.kebabCase(value || '');
          const defaultTemplateItem = topLevelItems.find(
            (item) =>
              item.default === true &&
              item.type === CoreEnums.PortalMenuItemType.RenderTemplate &&
              item.templateLink,
          );
          const defaultEmbedItem = topLevelItems.find(
            (item) =>
              item.default === true &&
              item.type === CoreEnums.PortalMenuItemType.EmbedHtml &&
              item.embedHtmlLink,
          );

          if (!hasNavigatedTemplate && defaultTemplateItem) {
            const menuSlug = toMenuSlug(defaultTemplateItem.title);
            sessionStorage.setItem('x-portal-template-navigated', 'true');
            history.replace({
              pathname: `/r/${menuSlug}`,
            });
          } else if (!hasNavigatedEmbed && defaultEmbedItem) {
            const menuSlug = toMenuSlug(defaultEmbedItem.title);
            sessionStorage.setItem('x-portal-embed-navigated', 'true');
            history.replace({
              pathname: `/e/${menuSlug}`,
            });
          }
        }
      } catch (e) {}
    },

    *logout({ payload }, { call, put, select }) {
      try {
        // Clean up local state immediately (don't wait for server)
        sessionStorage.removeItem('x-portal-embed-navigated');
        sessionStorage.removeItem('x-portal-template-navigated');
        removeSessionItem(STORAGE_KEYS.AUTHORITY);
        removeSessionItem(STORAGE_KEYS.AUTH_USER);
        removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
        if (!payload?.auto) {
          removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
        }

        if (payload?.isFailedLoginRedirect) {
          const portal = yield select((o) => o.companyPortal.portal);
          const loginUrl = portal ? `/user/login?portal=${portal.id}` : '/user/login';
          history.replace(loginUrl, portal || history.location.state);
          // Fire logout to server in background (don't wait)
          apiRoute({ route: 'auth/logout', method: 'post' }).catch(() => {});
          return;
        }

        if (payload?.auto) {
          removeSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
          yield put({ type: 'reset' });
          // Fire logout to server in background (don't wait)
          apiRoute({ route: 'auth/logout', method: 'post' }).catch(() => {});
        } else {
          // For full logout: remove portal-prefixed config and clear session
          const currentSession = getCurrentSession();
          if (currentSession?.type === SESSION_TYPES.PORTAL) {
            removeItem(
              SESSION_TYPES.PORTAL,
              currentSession.userId,
              currentSession.resourceId,
              STORAGE_KEYS.PORTAL_CONFIG,
            );
            setCurrentSession(null);
          }

          // For non-auto logout, wait for server before re-authenticating
          yield call(apiRoute, { route: 'auth/logout', method: 'post' });
          yield put({ type: 'companyPortal/auth', payload });
        }
      } catch (e) {
        // Even if logout fails, continue with the flow
        if (payload?.auto) {
          yield put({ type: 'reset' });
        } else {
          const retryTimes = yield select((o) => o.companyPortal.retryTimes);
          if (!retryTimes || retryTimes < 3) {
            yield put({ type: 'queryRetry', payload: 1 });
          }
        }
      }
    },

    *config({ payload }, { put }) {
      yield put({ type: 'queryConfig', payload });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'reset', payload });
    },
  },
  reducers: {
    queryAuth(state, { payload }) {
      const { portal, companyCode, portalCode, auto, userId, portalId } = payload;
      const config = { ...portal, companyCode, portalCode };

      // Write portal config to session-prefixed key if we have userId
      if (userId && portalId) {
        setItem(
          SESSION_TYPES.PORTAL,
          userId,
          String(portalId),
          STORAGE_KEYS.PORTAL_CONFIG,
          JSON.stringify(config),
        );
      } else {
        try {
          sessionStorage.setItem(STORAGE_KEYS.PORTAL_CONFIG, JSON.stringify(config));
        } catch (e) {}
      }

      sessionStorage.removeItem('x-portal-embed-navigated');
      sessionStorage.removeItem('x-portal-template-navigated');

      // Check if user is in 2FA flow
      const twoFactorRaw = getSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
      const isInTwoFactorFlow = twoFactorRaw ? JSON.parse(twoFactorRaw).required : false;

      if (
        !auto &&
        !sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS) &&
        !isInTwoFactorFlow &&
        !state.retryTimes
      ) {
        history.replace({ pathname: '/' }, config);
      }

      return {
        ...state,
        retryTimes: null,
        portal: config,
      };
    },

    queryMenu(state, { payload }) {
      const toMenuSlug = (value) => _.kebabCase(value || '');

      function menuItem(item) {
        const { path, id, ...more } = routing[item.type] || {};

        const hasChildren = item.subItems && item.subItems.length > 0;
        const orderedChildren = item.subItems?.sort(sortFn);
        const key = _.camelCase(item.title);
        const menuSlug = toMenuSlug(item.title || key);

        const fragments = id
          ? _(id)
              .split(',')
              .map((k) => item[k])
              .filter(Boolean)
              .join('/')
          : '';

        const fallbackPath =
          item.type === CoreEnums.PortalMenuItemType.EmbedHtml
            ? `/e/${menuSlug}`
            : item.type === CoreEnums.PortalMenuItemType.RenderTemplate
            ? `/r/${menuSlug}`
            : '';

        const resolvedPath = hasChildren
          ? `/${key}`
          : (path || item.link || fallbackPath || '') + fragments;

        return {
          ...more,
          name: item.title,
          key: _.camelCase(item.title),
          path: resolvedPath,
          children: _.map(orderedChildren, menuItem),
          type: item.type === CoreEnums.PortalMenuItemType.Link ? 'link' : item.type,
          embedHtmlLink: item.embedHtmlLink,
          templateLink: item.templateLink,
          menuSlug,
        };
      }

      const orderedItems = payload?.sort(sortFn)?.reduce((lhs, rhs) => {
        return _.concat(
          lhs,
          [
            rhs.menuItems && {
              type: 'session',
              title: rhs.title,
              icon: null,
            },
            ...(rhs.menuItems?.sort(sortFn) || []),
          ].filter(Boolean),
        );
      }, []);

      const menu = _.map(orderedItems, menuItem);

      try {
        const stored = getSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
        if (stored) {
          setSessionItem(
            STORAGE_KEYS.PORTAL_CONFIG,
            JSON.stringify({ ...JSON.parse(stored), menu }),
          );
        }
      } catch (e) {}

      return {
        ...state,
        portal: {
          ...state.portal,
          menu,
        },
      };
    },

    queryConfig(state, { payload }) {
      return {
        ...state,
        portal: payload,
      };
    },

    queryRetry(state, { payload }) {
      return {
        ...state,
        retryTimes: state.retryTimes + payload,
      };
    },

    reset() {
      return initialState;
    },
  },
};

export default Model;
